import { lazy, Suspense, useId, useMemo, useState } from "react";
import clsx from "clsx";
import { styled } from "@mui/material";
import { Link, Modal, ModalCloseButton, ModalContent, Typography } from "@kaltura/ds-react-components";
import { baseUrl, fetchKmsData, translate, WebcastHelper, WebcastStatus } from "@kaltura/mediaspace-shared-utils";
import { useButtonAnalytics, useCurrentTimeForAnimation } from "@kaltura/mediaspace-shared-hooks";
import { Box, useTheme } from "@kaltura/mediaspace-shared-styled";
import { ButtonClickAnalyticsType, KmsTypePresenter } from "@kaltura/mediaspace-shared-types";
import { ThemeProvider } from "@kaltura/mediaspace-shared-contexts/ThemeProvider";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { UserCard, UserSeeProfileButton } from "@kaltura/mediaspace-shared-user";
import { ScheduledSession } from "../../event-list/EventList";
import { EventListSkeleton } from "../../event-list/EventListSkeleton";

const EventList = lazy(() => import("../../event-list/EventList"));

const tmpBaseClassName = "kms-ds-group-user-modal";
const tmpPaperClassName = tmpBaseClassName + "-paper";

const baseClassName = "kms-ds-event-presenter-modal";
const paperClassName = baseClassName + "-paper";

export interface EventPresenterModalProps {
    open: boolean;
    onClose?: () => void;
    user: KmsTypePresenter;
}

export const EventPresenterModal = ({ open, onClose, user }: EventPresenterModalProps) => {
    const fetchSessions = useUserSessions(user.unlinkFromProfile ? null : user.id);

    const sendButtonAnalytics = useButtonAnalytics();

    const userCardId = useId();

    const handleClose = () => {
        onClose?.();
        sendButtonAnalytics("User modal box - Close modal", ButtonClickAnalyticsType.CLOSE);
    };

    return (
        <StyledModal
            className={clsx(baseClassName, tmpBaseClassName)}
            variant={"content"}
            open={open}
            onClose={handleClose}
            fullWidth={true}
            classes={{ paper: clsx(paperClassName, tmpPaperClassName) }}
            aria-labelledby={userCardId}
        >
            <ModalCloseButton variant={"pill"} onClick={handleClose} aria-label={translate("close")} />

            <ModalContent>
                <ErrorBoundary fallback={<UserModelCard user={user} userCardId={userCardId} />}>
                    <UserModelCard user={user} userCardId={userCardId} />
                    {!user.unlinkFromProfile && (
                        <Suspense fallback={<EventListSkeleton />}>
                            <EventPresenterModalContent user={user} fetchSessions={fetchSessions} />
                        </Suspense>
                    )}
                </ErrorBoundary>
            </ModalContent>
        </StyledModal>
    );
};

type UserModelCardProps = {
    user: KmsTypePresenter;
    userCardId: string;
};

const UserModelCard = ({ user, userCardId }: UserModelCardProps) => (
    <UserCard
        user={user}
        nameId={userCardId}
        size={"big"}
        showMoreElement={!user.unlinkFromProfile && user.link && <UserSeeProfileButton href={user.link} />}
        showMore={true}
    />
);

interface EventPresenterModalContentProps {
    user: KmsTypePresenter;
    fetchSessions: () => ScheduledSession[];
}

const EventPresenterModalContent = ({ user, fetchSessions }: EventPresenterModalContentProps) => {
    const sessions = fetchSessions();

    // Refresh the component each second to display live status according to the current time
    useCurrentTimeForAnimation(undefined, 1000);

    // Map sessions by live statuses
    const sessionsByStatus: Record<WebcastStatus, ScheduledSession[]> = {
        [WebcastStatus.noScheduling]: [],
        [WebcastStatus.past]: [],
        [WebcastStatus.live]: [],
        [WebcastStatus.future]: [],
    };
    for (const session of sessions) {
        const { schedulingData } = session;
        if (schedulingData) {
            sessionsByStatus[WebcastHelper.getLiveStatus(schedulingData)].push(session);
        }
    }
    const { past, live, future } = sessionsByStatus;

    // Show live/future sessions if available, otherwise show past sessions
    let filteredSessions: ScheduledSession[];
    let label: string;
    if (live.length || future.length) {
        label = translate("Scheduled sessions");
        filteredSessions = [...live, ...future];
    }
    else {
        label = translate("Previous sessions");
        filteredSessions = past;
    }

    /*
     * Substitute the breakpoints for the modal's content, so that it will always render the mobile view
     * (because the modal's max width is 790px)
     */
    const theme = useTheme();
    const contentTheme = useMemo(() => {
        return {
            ...theme.kaltura,
            breakpoints: {
                ...theme.kaltura.breakpoints,
                /*
                 * Set breakpoints to extra large numbers for "md" and above,
                 * so that rules like `breakpoints.down("md")` will hit all devices.
                 * It will eventually lead to rendering the "sm" version of the components on the large devices.
                 */
                xl: 100000,
                lg: 100000,
                md: 100000,
            },
        };
    }, [theme]);

    const sendButtonAnalytics = useButtonAnalytics();

    return (
        filteredSessions.length > 0 && (
            <StyledSessionsContainer>
                <StyledSessionsLabel variant={"body2Highlight"} component={"h3"}>
                    {label}
                </StyledSessionsLabel>

                <ThemeProvider
                    scoped={true}
                    cssPrefix={"kms-presenter-modal"}
                    overrides={contentTheme}
                    injectFirst={false}
                >
                    <StyledSessionsList sessions={filteredSessions.slice(0, 2)} hidePresenterModal={true} />
                </ThemeProvider>

                <StyledSessionsLink
                    href={user.link}
                    size={"large"}
                    onClick={() =>
                        sendButtonAnalytics("User modal box - See all sessions", ButtonClickAnalyticsType.LINK)
                    }
                >
                    {translate("See all sessions")}
                </StyledSessionsLink>
            </StyledSessionsContainer>
        )
    );
};

/**
 * Lazy-load user's sessions from the backend.
 *
 * The user ID from the prop could change (when showing the modal for different users),
 * so the state stores a map of sessions by user ID.
 *
 * When the input user ID changes, the component checks if
 * the map already contains the data for the requested user,
 * and fetches the data from the backend if it's not there yet.
 *
 * When userId is null (unlinkFromProfile users), no sessions are rendered,
 * so no backend request is made for such users (early returns []).
 */
const useUserSessions = (userId: string | null) => {
    const [sessionsMap] = useState<Record<string, () => ScheduledSession[]>>({});

    if (!userId) {
        return () => [] as ScheduledSession[];
    }

    /*
     * Start fetching user sessions if didn't start doing it yet.
     *
     * Note: no need to use React hooks to update the state:
     * - sessionsMap is an object, so it could be modified in place.
     * - Component already re-rendered if userId changed, so UI will update.
     * - We need to have the new data fetcher available immediately,
     *   not on the next render, so useEffect() is not an option.
     */
    // eslint-disable-next-line react-hooks/immutability
    sessionsMap[userId] =
        sessionsMap[userId] ?? fetchKmsData<ScheduledSession[]>(baseUrl + "/sessions/get-user-sessions", { userId });

    return sessionsMap[userId];
};

const StyledModal = styled(Modal)({
    [`.${paperClassName}`]: {
        maxWidth: 790,
    },
});

const StyledSessionsContainer = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(4),
}));

const StyledSessionsLabel = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.tone1,
}));

const StyledSessionsList = styled(EventList)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const StyledSessionsLink = styled(Link)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));
